import { useEffect, type RefObject } from "react";

/**
 * A side column is pinned under the top bar only while the whole of it fits
 * the pane. Otherwise it scrolls with the page. It never scrolls on its own.
 *
 * WHY. The columns used to be capped at the window's height and scroll inside
 * themselves, which drew a second scroll bar in the page (Iwan, 14 Oct, on the
 * quote's Totals: "This scroll bar needs removing"; Gabe then ruled that every
 * desk side column behaves the same way). Uncapped and always pinned, a column
 * taller than the pane would have its foot out of reach until the list beside
 * it ended: Materials' Import, the reason #205 capped the column (Bugbot, "Tall
 * side column cannot be scrolled"). Pinned only when it fits, and otherwise
 * part of the page, nothing in a column is ever out of reach.
 *
 * HOW. The stylesheet does the pinning, on `[data-pin]`. This only says whether
 * the column fits: its height against the scrolling pane (`.main`, or the
 * window when there is none) less the column's own sticky `top` and a 16px
 * gutter. It measures when the column or the pane changes size, never on
 * scroll. Until it has measured, the column is not pinned, so a column that
 * cannot be measured still scrolls with the page.
 *
 * THE ONE SANCTIONED MEASUREMENT. desk-ui's rule is that nothing measures and
 * nothing observes; Gabe made this hook the exception (14 Oct). desk-ui's check
 * lets this file, and only this file, use ResizeObserver and getComputedStyle,
 * and lets page.tsx call nothing that measures but this hook.
 */

/** The room kept under a pinned column, so its foot never sits on the pane's edge. */
export const PIN_GUTTER = 16;

/**
 * Pin `el` while it fits its pane, and keep that true as either changes size.
 * Returns the function that stops watching and unpins it.
 */
export function pinWhenFits(el: HTMLElement): () => void {
  const view = el.ownerDocument.defaultView;
  if (!view) return () => undefined;
  const pane = el.closest<HTMLElement>(".main");
  const fit = (): void => {
    // The offset is read while the column is pinned, so `top` resolves as the
    // sticky offset it will be (Bugbot on #232). Read while it was static, a
    // browser may answer `auto`, which counted the top bar's 74px as 0 and
    // pinned a column 74px too tall to fit. Setting and reading is one
    // synchronous step with no paint between, so nothing flickers.
    el.setAttribute("data-pin", "");
    const top = parseFloat(view.getComputedStyle(el).top) || 0;
    const room = (pane ? pane.clientHeight : view.innerHeight) - top - PIN_GUTTER;
    if (el.offsetHeight > room) el.removeAttribute("data-pin");
  };
  fit();
  // A browser without ResizeObserver (and jsdom, where the console's check
  // runs) still measures once and whenever the window changes size.
  const Watch = (view as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
  const watch = typeof Watch === "function" ? new Watch(fit) : null;
  watch?.observe(el);
  if (pane) watch?.observe(pane);
  view.addEventListener("resize", fit);
  return () => {
    watch?.disconnect();
    view.removeEventListener("resize", fit);
    el.removeAttribute("data-pin");
  };
}

/**
 * pinWhenFits for a column a component draws. `deps` names what mounts or
 * replaces the column (data arriving, the column appearing), so the watch moves
 * to the new element; a change of size inside the same element needs no dep.
 */
export function usePinWhenFits(ref: RefObject<HTMLElement>, deps: readonly unknown[] = []): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return pinWhenFits(el);
    // The caller names what re-renders the column; the body reads the element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
}
